import { useMutation, useQuery } from '@tanstack/react-query';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { Button } from '../../../web/components/Button';
import { StarRating } from '../../../web/components/surveys/StarRating';
import { useApiClientContext } from '../../context';
import { Survey } from '../SurveyResponses/shared/Survey';
import { styles } from './styles';

interface SupportRatingContext {
	alreadySubmitted: boolean;
	rating: number | null;
}

interface SupportRatingResult {
	alreadySubmitted: boolean;
	rating: number;
}

const MAX_FEEDBACK_LENGTH = 2_000;

export const SupportRating = () => {
	const { params } = useRouteMatch<{ ratingToken: string }>();
	const token = params.ratingToken;
	const { apiClient } = useApiClientContext();
	const starStyles = React.useRef<StyleDeclarationValue[]>([styles.star]).current;
	const [rating, setRating] = React.useState(0);
	const [feedback, setFeedback] = React.useState('');
	const [validationError, setValidationError] = React.useState<string | null>(null);

	const contextQuery = useQuery({
		queryKey: ['support-rating', token],
		queryFn: () => apiClient.get<SupportRatingContext>(`support/ratings?token=${encodeURIComponent(token)}`),
		enabled: !!token,
		retry: false,
	});

	const submit = useMutation({
		mutationFn: () =>
			apiClient.post<SupportRatingResult>(`support/ratings?token=${encodeURIComponent(token)}`, {
				rating,
				feedback: feedback.trim() || null,
			}),
	});

	const onSubmit = () => {
		if (rating < 1 || rating > 5) {
			setValidationError('Please choose a star rating.');
			return;
		}
		setValidationError(null);
		submit.mutate();
	};

	const submittedRating = submit.data?.rating ?? contextQuery.data?.rating;
	const wasAlreadySubmitted = contextQuery.data?.alreadySubmitted || submit.data?.alreadySubmitted;
	const isComplete = submit.isSuccess || contextQuery.data?.alreadySubmitted;

	return (
		<Survey unavailableTitle='Levitate Support' styles={[styles.page]} contentStyles={[styles.content]}>
			<div className={css(styles.card)}>
				{contextQuery.isPending ? (
					<p className={css(styles.status)}>Loading your rating form…</p>
				) : contextQuery.isError ? (
					<>
						<h1 className={css(styles.title)}>This rating link is unavailable</h1>
						<p className={css(styles.status)}>The link may be invalid or expired.</p>
					</>
				) : isComplete ? (
					<>
						<h1 className={css(styles.title)}>Thank you for your feedback</h1>
						<p className={css(styles.status)}>
							{wasAlreadySubmitted
								? `This conversation has already been rated${submittedRating ? ` ${submittedRating}/5` : ''}.`
								: 'Your rating helps us improve our support.'}
						</p>
					</>
				) : (
					<>
						<h1 className={css(styles.title)}>How was your support experience?</h1>
						<p className={css(styles.description)}>Choose a rating from one to five stars.</p>
						<div className={css(styles.rating)}>
							<StarRating
								animateRatingChanges
								fillColor='#F3CE0D'
								onValueChanged={value => {
									setRating(value);
									setValidationError(null);
								}}
								starStyles={starStyles}
								total={5}
								value={rating}
								showEmoji={false}
							/>
						</div>
						<textarea
							className={css(styles.feedback)}
							value={feedback}
							onChange={event => setFeedback(event.target.value.slice(0, MAX_FEEDBACK_LENGTH))}
							maxLength={MAX_FEEDBACK_LENGTH}
							placeholder='Anything else you would like us to know? (optional)'
							aria-label='Additional feedback'
						/>
						<span className={css(styles.characterCount)}>
							{feedback.length}/{MAX_FEEDBACK_LENGTH}
						</span>
						{validationError && <p className={css(styles.error)}>{validationError}</p>}
						{submit.isError && <p className={css(styles.error)}>We couldn’t save your rating. Please try again.</p>}
						<Button
							kind='primary'
							label={submit.isPending ? 'Submitting…' : 'Submit rating'}
							onClick={onSubmit}
							disabled={submit.isPending}
						/>
					</>
				)}
			</div>
		</Survey>
	);
};
